// Aug-2026 (User-Wunsch) — Newsletter-Modul: Empfängerlisten, Vorlagen,
// Kampagnen-Versand über newsletter@ (SMTP) mit Abmelde-Link.
// Jul-2026: Visueller Block-Editor (Text/Bild/Button/Fahrzeug-Kacheln) +
// Öffnungs-/Klick-Statistik pro Kampagne.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Mail, Send, Save, Trash2, RefreshCw, ArrowLeft, Loader2, Type, Image as ImageIcon,
    MousePointerClick, Bike, ArrowUp, ArrowDown, X, Search, Eye, Code, Clock,
} from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";

const AUDIENCES = [
    { key: "b2b", label: "Händler (B2B)" },
    { key: "b2c", label: "Privatkunden (B2C)" },
    { key: "customers", label: "Werkstatt-/Verkaufs-Kunden" },
    { key: "subscribers", label: "Newsletter-Abonnenten (Anmeldeformular)" },
    { key: "custom", label: "Eigene Liste (E-Mails einfügen)" },
];

const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export function renderBlocksHtml(blocks) {
    const origin = window.location.origin;
    return (blocks || []).map((b) => {
        if (b.type === "text") {
            return `<p style="font-size:14px;line-height:1.65;color:#111827;margin:0 0 16px">${esc(b.text).replace(/\n/g, "<br>")}</p>`;
        }
        if (b.type === "image") {
            return b.url ? `<img src="${b.url}" alt="${esc(b.alt)}" style="width:100%;border-radius:6px;margin:0 0 16px;display:block">` : "";
        }
        if (b.type === "button") {
            return b.url ? `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:0 auto 20px"><tr><td style="background:#111827;border-radius:6px"><a href="${b.url}" style="display:inline-block;padding:12px 28px;color:#FFCC00;font-weight:bold;font-size:14px;text-decoration:none">${esc(b.label || "Mehr erfahren")}</a></td></tr></table>` : "";
        }
        if (b.type === "vehicles") {
            const cells = (b.items || []).map((v) =>
                `<td width="50%" style="padding:6px;vertical-align:top"><div style="border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;background:#fff">${v.image ? `<img src="${v.image}" style="width:100%;height:140px;object-fit:cover;display:block">` : ""}<div style="padding:10px"><div style="font-size:13px;font-weight:bold;color:#111827">${esc(v.title)}</div><div style="font-size:15px;font-weight:900;color:#111827;margin:4px 0 8px">${v.price ? Number(v.price).toLocaleString("de-DE") + " €" : ""}</div><a href="${origin}/listing/${v.id}" style="display:inline-block;background:#FFCC00;color:#111827;font-size:12px;font-weight:bold;padding:7px 14px;border-radius:4px;text-decoration:none">Fahrzeug ansehen</a></div></div></td>`);
            const rows = [];
            for (let i = 0; i < cells.length; i += 2) rows.push(`<tr>${cells[i]}${cells[i + 1] || '<td width="50%"></td>'}</tr>`);
            return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px">${rows.join("")}</table>`;
        }
        return "";
    }).join("");
}

const inputCls = "mt-1 w-full text-sm rounded-sm border border-gray-300 px-3 py-2 focus:border-[#111827] focus:outline-none";

function VehiclePicker({ block, onChange }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [busy, setBusy] = useState(false);
    const search = async () => {
        setBusy(true);
        try {
            const r = await api.get(`/admin/newsletter/listings?q=${encodeURIComponent(q)}`);
            setResults(r.data.items || []);
            if (!(r.data.items || []).length) toast.info("Keine aktiven Inserate mit Bild gefunden.");
        } catch { toast.error("Suche fehlgeschlagen"); }
        setBusy(false);
    };
    const add = (v) => {
        if ((block.items || []).some((i) => i.id === v.id)) return;
        if ((block.items || []).length >= 4) { toast.error("Max. 4 Fahrzeuge pro Block."); return; }
        onChange({ ...block, items: [...(block.items || []), v] });
    };
    return (
        <div className="space-y-2">
            <div className="flex gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
                    placeholder="Bestand durchsuchen (Marke, Modell, Titel)…"
                    className="flex-1 text-sm rounded-sm border border-gray-300 px-3 py-2" data-testid="nl-vehicle-search-input" />
                <Button type="button" variant="outline" onClick={search} disabled={busy} className="rounded-sm" data-testid="nl-vehicle-search-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
            </div>
            {results.length > 0 && (
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-sm divide-y divide-gray-100" data-testid="nl-vehicle-results">
                    {results.map((v) => (
                        <button key={v.id} type="button" onClick={() => add(v)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-gray-50 text-xs"
                            data-testid={`nl-vehicle-add-${v.id}`}>
                            {v.image && <img src={v.image} alt="" className="w-10 h-8 object-cover rounded-sm shrink-0" />}
                            <span className="flex-1 truncate font-bold">{v.title}</span>
                            <span className="font-mono-num whitespace-nowrap">{v.price ? `${Number(v.price).toLocaleString("de-DE")} €` : ""}</span>
                        </button>
                    ))}
                </div>
            )}
            {(block.items || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="nl-vehicle-selected">
                    {block.items.map((v) => (
                        <span key={v.id} className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 rounded-sm px-2 py-1 text-[11px] font-bold">
                            {v.title}
                            <button type="button" onClick={() => onChange({ ...block, items: block.items.filter((i) => i.id !== v.id) })}
                                className="text-gray-400 hover:text-red-600" data-testid={`nl-vehicle-remove-${v.id}`}><X className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function BlockCard({ block, index, total, onChange, onMove, onRemove }) {
    const icons = { text: Type, image: ImageIcon, button: MousePointerClick, vehicles: Bike };
    const labels = { text: "Text", image: "Bild", button: "Button", vehicles: "Fahrzeug-Kacheln" };
    const Icon = icons[block.type] || Type;
    return (
        <div className="border border-gray-200 rounded-sm p-3 bg-gray-50 space-y-2" data-testid={`nl-block-${index}`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-gray-500" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex-1">{labels[block.type]}</span>
                <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} className="text-gray-400 hover:text-gray-700 disabled:opacity-30" data-testid={`nl-block-up-${index}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)} className="text-gray-400 hover:text-gray-700 disabled:opacity-30" data-testid={`nl-block-down-${index}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => onRemove(index)} className="text-gray-400 hover:text-red-600" data-testid={`nl-block-remove-${index}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            {block.type === "text" && (
                <textarea value={block.text || ""} onChange={(e) => onChange({ ...block, text: e.target.value })} rows={4}
                    placeholder="Ihr Text…" className="w-full text-sm rounded-sm border border-gray-300 px-3 py-2" data-testid={`nl-block-text-${index}`} />
            )}
            {block.type === "image" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input value={block.url || ""} onChange={(e) => onChange({ ...block, url: e.target.value })}
                        placeholder="Bild-URL (https://…)" className="text-sm rounded-sm border border-gray-300 px-3 py-2" data-testid={`nl-block-image-url-${index}`} />
                    <input value={block.alt || ""} onChange={(e) => onChange({ ...block, alt: e.target.value })}
                        placeholder="Beschreibung (Alt-Text)" className="text-sm rounded-sm border border-gray-300 px-3 py-2" data-testid={`nl-block-image-alt-${index}`} />
                    {block.url && <img src={block.url} alt="" className="col-span-full max-h-32 rounded-sm object-cover" />}
                </div>
            )}
            {block.type === "button" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input value={block.label || ""} onChange={(e) => onChange({ ...block, label: e.target.value })}
                        placeholder="Button-Text (z. B. Jetzt entdecken)" className="text-sm rounded-sm border border-gray-300 px-3 py-2" data-testid={`nl-block-button-label-${index}`} />
                    <input value={block.url || ""} onChange={(e) => onChange({ ...block, url: e.target.value })}
                        placeholder="Link (https://…)" className="text-sm rounded-sm border border-gray-300 px-3 py-2" data-testid={`nl-block-button-url-${index}`} />
                </div>
            )}
            {block.type === "vehicles" && <VehiclePicker block={block} onChange={(b) => onChange(b)} />}
        </div>
    );
}

export default function AdminNewsletterPage() {
    const [audiences, setAudiences] = useState({});
    const [templates, setTemplates] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [editorMode, setEditorMode] = useState("visual"); // "visual" | "html"
    const [blocks, setBlocks] = useState([{ type: "text", text: "" }]);
    const [showPreview, setShowPreview] = useState(false);
    const [audience, setAudience] = useState("b2b");
    const [customEmails, setCustomEmails] = useState("");
    const [tplName, setTplName] = useState("");
    const [testEmail, setTestEmail] = useState("");
    const [scheduledAt, setScheduledAt] = useState("");
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get("/admin/newsletter/audiences").then((r) => setAudiences(r.data)).catch(() => {});
        api.get("/admin/newsletter/templates").then((r) => setTemplates(r.data.items || [])).catch(() => {});
        api.get("/admin/newsletter/campaigns").then((r) => setCampaigns(r.data.items || [])).catch(() => {});
    };
    useEffect(load, []);

    const buildHtml = () => editorMode === "visual" ? renderBlocksHtml(blocks) : body.replace(/\n/g, "<br>");
    const hasContent = () => editorMode === "visual"
        ? blocks.some((b) => (b.type === "text" && b.text?.trim()) || (b.type === "image" && b.url) || (b.type === "button" && b.url) || (b.type === "vehicles" && b.items?.length))
        : !!body.trim();

    const updateBlock = (i, b) => setBlocks((prev) => prev.map((x, idx) => (idx === i ? b : x)));
    const moveBlock = (i, dir) => setBlocks((prev) => {
        const next = [...prev];
        [next[i], next[i + dir]] = [next[i + dir], next[i]];
        return next;
    });
    const removeBlock = (i) => setBlocks((prev) => prev.filter((_, idx) => idx !== i));
    const addBlock = (type) => setBlocks((prev) => [...prev, type === "vehicles" ? { type, items: [] } : { type }]);

    const saveTemplate = async () => {
        if (!tplName.trim()) { toast.error("Bitte Vorlagen-Namen eingeben."); return; }
        try {
            await api.post("/admin/newsletter/templates", {
                name: tplName.trim(), subject, body_html: buildHtml(),
                blocks: editorMode === "visual" ? blocks : null,
            });
            toast.success("Vorlage gespeichert.");
            setTplName("");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    };

    const applyTemplate = (t) => {
        setSubject(t.subject || "");
        if (t.blocks?.length) { setBlocks(t.blocks); setEditorMode("visual"); }
        else { setBody((t.body_html || "").replace(/<br\s*\/?>/g, "\n")); setEditorMode("html"); }
        toast.info(`Vorlage „${t.name}" geladen.`);
    };

    const send = async (test = false) => {
        if (!subject.trim() || !hasContent()) { toast.error("Betreff und Inhalt sind Pflicht."); return; }
        if (test && !testEmail.trim()) { toast.error("Test-Adresse eingeben."); return; }
        if (!test) {
            const n = audience === "custom"
                ? customEmails.split(/[,;\n]/).filter((e) => e.includes("@")).length
                : audiences[audience] ?? "?";
            const when = scheduledAt ? `geplant für ${new Date(scheduledAt).toLocaleString("de-DE")}` : "sofort";
            if (!window.confirm(`Newsletter an ${n} Empfänger (${AUDIENCES.find((a) => a.key === audience)?.label}) senden — ${when}?`)) return;
        }
        setBusy(true);
        try {
            const r = await api.post("/admin/newsletter/campaigns", {
                subject, body_html: buildHtml(), audience,
                custom_emails: customEmails, test_email: test ? testEmail.trim() : "",
                scheduled_at: !test && scheduledAt ? new Date(scheduledAt).toISOString() : "",
            });
            if (r.data.scheduled_at) {
                toast.success(`Kampagne geplant für ${new Date(r.data.scheduled_at).toLocaleString("de-DE")} — ${r.data.total} Empfänger.`);
                setScheduledAt("");
            } else {
                toast.success(test ? "Testmail wird gesendet." : `Kampagne gestartet — ${r.data.total} Empfänger.`);
            }
            setTimeout(load, 1500);
        } catch (e) { toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen"); }
        setBusy(false);
    };

    const cancelCampaign = async (id) => {
        if (!window.confirm("Geplante Kampagne wirklich abbrechen?")) return;
        try {
            await api.post(`/admin/newsletter/campaigns/${id}/cancel`);
            toast.success("Kampagne abgebrochen.");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Abbrechen fehlgeschlagen"); }
    };

    const openDetail = async (id) => {
        if (detail?.campaign?.id === id) { setDetail(null); return; }
        try {
            const r = await api.get(`/admin/newsletter/campaigns/${id}/stats`);
            setDetail(r.data);
        } catch { toast.error("Details konnten nicht geladen werden"); }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="admin-newsletter-page">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h1 className="font-display text-2xl font-black text-[#111827] flex items-center gap-2"><Mail className="w-6 h-6" /> Newsletter</h1>
                    <p className="text-sm text-gray-500 mt-1">Visueller Editor, Empfängerlisten, Vorlagen — Versand über newsletter@ mit Abmelde-Link, Öffnungs- &amp; Klick-Tracking.</p>
                </div>
                <Link to="/admin" className="text-sm font-bold text-gray-600 hover:text-[#111827] inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Admin-Übersicht</Link>
            </div>

            <section className="bg-white border border-gray-200 rounded-sm p-5 space-y-4" data-testid="newsletter-compose">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Zielgruppe</label>
                        <select value={audience} onChange={(e) => setAudience(e.target.value)}
                            className={`${inputCls} bg-white`} data-testid="newsletter-audience-select">
                            {AUDIENCES.map((a) => (
                                <option key={a.key} value={a.key}>
                                    {a.label}{audiences[a.key] != null ? ` (${audiences[a.key]})` : ""}
                                </option>
                            ))}
                        </select>
                        {audiences.optout_total > 0 && (
                            <p className="text-[10px] text-gray-400 mt-0.5">{audiences.optout_total} Abmeldungen werden automatisch ausgeschlossen.</p>
                        )}
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Betreff</label>
                        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="z. B. Neue Saison-Angebote bei TwoWheels"
                            className={inputCls} data-testid="newsletter-subject-input" />
                    </div>
                </div>
                {audience === "custom" && (
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Eigene Empfängerliste (Komma / Zeilenumbruch)</label>
                        <textarea value={customEmails} onChange={(e) => setCustomEmails(e.target.value)} rows={3}
                            placeholder="kunde1@mail.de, kunde2@mail.de"
                            className={`${inputCls} font-mono`} data-testid="newsletter-custom-emails" />
                    </div>
                )}

                {/* Editor-Umschalter */}
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="inline-flex rounded-sm border border-gray-300 overflow-hidden" data-testid="newsletter-editor-switch">
                        <button type="button" onClick={() => setEditorMode("visual")}
                            className={`px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 ${editorMode === "visual" ? "bg-[#111827] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                            data-testid="newsletter-mode-visual"><Eye className="w-3.5 h-3.5" /> Visueller Editor</button>
                        <button type="button" onClick={() => setEditorMode("html")}
                            className={`px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 ${editorMode === "html" ? "bg-[#111827] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                            data-testid="newsletter-mode-html"><Code className="w-3.5 h-3.5" /> Text / HTML</button>
                    </div>
                    <div className="flex-1" />
                    <button type="button" onClick={() => setShowPreview((v) => !v)}
                        className="text-xs font-bold text-gray-600 hover:text-[#111827] inline-flex items-center gap-1"
                        data-testid="newsletter-preview-toggle"><Eye className="w-3.5 h-3.5" /> {showPreview ? "Vorschau ausblenden" : "Vorschau anzeigen"}</button>
                </div>

                {editorMode === "visual" ? (
                    <div className="space-y-3" data-testid="newsletter-visual-editor">
                        {blocks.map((b, i) => (
                            <BlockCard key={i} block={b} index={i} total={blocks.length}
                                onChange={(nb) => updateBlock(i, nb)} onMove={moveBlock} onRemove={removeBlock} />
                        ))}
                        <div className="flex flex-wrap gap-2">
                            <Button type="button" variant="outline" size="sm" onClick={() => addBlock("text")} className="rounded-sm text-xs" data-testid="nl-add-text"><Type className="w-3.5 h-3.5 mr-1" /> Text</Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => addBlock("image")} className="rounded-sm text-xs" data-testid="nl-add-image"><ImageIcon className="w-3.5 h-3.5 mr-1" /> Bild</Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => addBlock("button")} className="rounded-sm text-xs" data-testid="nl-add-button"><MousePointerClick className="w-3.5 h-3.5 mr-1" /> Button</Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => addBlock("vehicles")} className="rounded-sm text-xs" data-testid="nl-add-vehicles"><Bike className="w-3.5 h-3.5 mr-1" /> Fahrzeuge aus Bestand</Button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Inhalt (Text oder HTML)</label>
                        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10}
                            placeholder={"Hallo,\n\nhier die Neuigkeiten von TwoWheels …"}
                            className={inputCls} data-testid="newsletter-body-input" />
                    </div>
                )}

                {showPreview && (
                    <div className="border border-gray-200 rounded-sm bg-gray-100 p-4" data-testid="newsletter-preview">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Vorschau</div>
                        <div className="bg-white rounded-sm max-w-xl mx-auto p-5" dangerouslySetInnerHTML={{ __html: buildHtml() }} />
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                    <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Test an eigene Adresse…"
                        className="text-sm rounded-sm border border-gray-300 px-3 py-2 w-60" data-testid="newsletter-test-email" />
                    <Button variant="outline" disabled={busy} onClick={() => send(true)} className="rounded-sm" data-testid="newsletter-test-btn">
                        Testmail senden
                    </Button>
                    <div className="flex-1" />
                    <input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Als Vorlage speichern als…"
                        className="text-sm rounded-sm border border-gray-300 px-3 py-2 w-56" data-testid="newsletter-tpl-name" />
                    <Button variant="outline" disabled={busy} onClick={saveTemplate} className="rounded-sm" data-testid="newsletter-tpl-save-btn">
                        <Save className="w-4 h-4 mr-1" /> Vorlage
                    </Button>
                </div>
                <div className="flex flex-wrap items-end gap-2 border-t border-gray-100 pt-3">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Später senden (optional)</label>
                        <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
                            min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)}
                            className="mt-0.5 text-sm rounded-sm border border-gray-300 px-3 py-2" data-testid="newsletter-schedule-input" />
                    </div>
                    {scheduledAt && (
                        <button type="button" onClick={() => setScheduledAt("")} className="text-xs text-gray-500 hover:text-red-600 pb-2.5" data-testid="newsletter-schedule-clear">Zurücksetzen</button>
                    )}
                    <div className="flex-1" />
                    <Button disabled={busy} onClick={() => send(false)} className="bg-[#111827] text-[#FFCC00] hover:bg-black rounded-sm font-bold" data-testid="newsletter-send-btn">
                        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : scheduledAt ? <Clock className="w-4 h-4 mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                        {scheduledAt ? "Kampagne planen" : "Kampagne senden"}
                    </Button>
                </div>
            </section>

            {templates.length > 0 && (
                <section className="bg-white border border-gray-200 rounded-sm p-5" data-testid="newsletter-templates">
                    <h2 className="font-black text-base text-[#111827] mb-2">Vorlagen</h2>
                    <div className="flex flex-wrap gap-2">
                        {templates.map((t) => (
                            <span key={t.id} className="inline-flex items-center gap-1.5 bg-gray-100 border border-gray-200 rounded-sm px-2 py-1 text-xs font-bold">
                                <button type="button" onClick={() => applyTemplate(t)} className="hover:underline" data-testid={`newsletter-tpl-${t.id}`}>{t.name}</button>
                                <button type="button" onClick={async () => { await api.delete(`/admin/newsletter/templates/${t.id}`); load(); }}
                                    className="text-gray-400 hover:text-red-600" title="Vorlage löschen" data-testid={`newsletter-tpl-del-${t.id}`}>
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                </section>
            )}

            <section className="bg-white border border-gray-200 rounded-sm p-5" data-testid="newsletter-campaigns">
                <div className="flex items-center justify-between mb-2">
                    <h2 className="font-black text-base text-[#111827]">Kampagnen</h2>
                    <button onClick={load} className="text-gray-400 hover:text-gray-700" title="Aktualisieren" data-testid="newsletter-refresh-btn"><RefreshCw className="w-4 h-4" /></button>
                </div>
                {campaigns.length === 0 ? (
                    <p className="text-xs text-gray-400">Noch keine Kampagnen.</p>
                ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[640px]">
                        <thead><tr className="text-left text-gray-500 uppercase tracking-wider">
                            <th className="py-1.5">Betreff</th><th>Zielgruppe</th><th>Empfänger</th><th>Gesendet</th><th>Fehler</th>
                            <th title="Eindeutige Öffnungen (Tracking-Pixel)">Öffnungen</th><th title="Empfänger mit mindestens einem Klick">Klicks</th>
                            <th>Status</th><th>Datum</th><th></th>
                        </tr></thead>
                        <tbody>
                            {campaigns.map((c) => (
                                <tr key={c.id} className="border-t border-gray-100" data-testid={`newsletter-campaign-${c.id}`}>
                                    <td className="py-1.5 font-bold">{c.subject}</td>
                                    <td>{c.audience}</td>
                                    <td className="font-mono-num">{c.total}</td>
                                    <td className="font-mono-num text-emerald-700">{c.sent}</td>
                                    <td className="font-mono-num text-red-600">{c.failed}</td>
                                    <td className="font-mono-num font-bold" data-testid={`newsletter-opens-${c.id}`}>
                                        {c.opens ?? 0}{c.sent > 0 && c.opens > 0 ? ` (${Math.round((c.opens / c.sent) * 100)} %)` : ""}
                                    </td>
                                    <td className="font-mono-num font-bold" data-testid={`newsletter-clicks-${c.id}`}>{c.clicks ?? 0}</td>
                                    <td>{c.status === "done" ? "✓ fertig"
                                        : c.status === "scheduled" ? `⏱ geplant · ${new Date(c.scheduled_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}`
                                        : c.status === "canceled" ? "✕ abgebrochen" : "läuft…"}</td>
                                    <td className="text-gray-400">{(c.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                    <td className="text-right whitespace-nowrap">
                                        <button type="button" onClick={() => openDetail(c.id)}
                                            className="text-[11px] font-bold text-gray-600 hover:text-[#111827] underline"
                                            data-testid={`newsletter-detail-btn-${c.id}`}>Details</button>
                                        {c.status === "scheduled" && (
                                            <button type="button" onClick={() => cancelCampaign(c.id)}
                                                className="ml-2 text-[11px] font-bold text-red-600 hover:text-red-800 underline"
                                                data-testid={`newsletter-cancel-btn-${c.id}`}>Abbrechen</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
                {detail && (
                    <div className="mt-4 border-t-2 border-[#111827] pt-3 space-y-3" data-testid="newsletter-detail-panel">
                        <div className="flex items-center justify-between">
                            <div className="text-sm font-black text-[#111827]">Details: {detail.campaign.subject}</div>
                            <button type="button" onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-700" data-testid="newsletter-detail-close"><X className="w-4 h-4" /></button>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Geklickte Links</div>
                            {detail.links.length === 0 ? <p className="text-xs text-gray-400">Noch keine Klicks.</p> : (
                                <div className="overflow-x-auto">
                                <table className="w-full text-xs min-w-[480px]" data-testid="newsletter-detail-links">
                                    <thead><tr className="text-left text-gray-500"><th className="py-1">Link</th><th>Klicks gesamt</th><th>Empfänger</th></tr></thead>
                                    <tbody>{detail.links.map((l, i) => (
                                        <tr key={i} className="border-t border-gray-100">
                                            <td className="py-1 max-w-md truncate"><a href={l.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">{l.url}</a></td>
                                            <td className="font-mono-num font-bold">{l.clicks_total}</td>
                                            <td className="font-mono-num">{l.unique_recipients}</td>
                                        </tr>
                                    ))}</tbody>
                                </table>
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Empfänger-Aktivität ({detail.recipients.length})</div>
                            {detail.recipients.length === 0 ? <p className="text-xs text-gray-400">Noch keine Öffnungen oder Klicks erfasst.</p> : (
                                <div className="overflow-x-auto max-h-56 overflow-y-auto">
                                <table className="w-full text-xs min-w-[520px]" data-testid="newsletter-detail-recipients">
                                    <thead><tr className="text-left text-gray-500"><th className="py-1">Empfänger</th><th>Öffnungen</th><th>Klicks</th><th>Zuletzt geklickt</th></tr></thead>
                                    <tbody>{detail.recipients.map((r, i) => (
                                        <tr key={i} className="border-t border-gray-100">
                                            <td className="py-1 font-bold">{r.email}</td>
                                            <td className="font-mono-num">{r.open_count || 0}</td>
                                            <td className="font-mono-num">{r.click_count || 0}</td>
                                            <td className="max-w-xs truncate text-gray-500">{r.last_click_url || "—"}</td>
                                        </tr>
                                    ))}</tbody>
                                </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
